import {
  createAction,
  createSlice,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";

import { infoSnack, successSnack, errorSnack } from "../snackbar/snackbarSlice";
import {
  preSignedURLMedia,
  deleteProductImage,
  updateProductImage,
} from "./productImagesSlice";

import axios from "axios";
const baseURL = process.env.REACT_APP_API_URL;

export const resetProduct = createAction("products/resetProduct");

export const preSignedURL = createAsyncThunk(
  "products/preSignedURL",
  async (args, thunkAPI) => {
    const config = {
      headers: {
        Authorization: `Bearer ${args.token}`,
      },
    };
    try {
      //full options
      const response = await axios.post(
        `${baseURL}/v1/admin/pre_signed_url`,
        {
          objectName: args.objectName,
          objectType: args.objectType,
          objectContentType: args.contentType,
          download: args.download,
        },
        config
      );
      if (response.data.preSignedURL && !args.download) {
        thunkAPI.dispatch(
          uploadCSV({
            token: args.token,
            csvFile: args.csvFile,
            url: response.data.preSignedURL,
            contentType: args.contentType,
            localization: args.localization,
          })
        );
      }
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error creating pre-signed url for AWS upload"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const uploadCSV = createAsyncThunk(
  "products/uploadCSV",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: {
          // Authorization: `Bearer ${args.token}`,
          "Content-Type": args.contentType,
        },
        //
      };
      const response = await axios.put(`${args.url}`, args.csvFile, config);
      if (response.status === 200) {
        thunkAPI.dispatch(
          bulkUpload({
            token: args.token,
            csvObj: args.csvFile.name,
            localization: args.localization,
          })
        );
      }

      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error uploading CSV to AWS"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const bulkUpload = createAsyncThunk(
  "products/bulkUpload",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${args.token}`,
          "Accept-Language": args.localization,
        },
        //
      };
      const response = await axios.post(
        `${baseURL}/v1/admin/products/bulk`,
        { csvObj: args.csvObj },
        config
      );

      if (response.data.statusToken) {
        thunkAPI.dispatch(
          bulkUploadStatusPoll({
            token: args.token,
            statusToken: response.data.statusToken,
            localization: args.localization,
            csvObj: args.csvObj,
          })
        );

        thunkAPI.dispatch(setFeedback({ failures: [] }));
        thunkAPI.dispatch(setBulkDetails({ name: args.csvObj }));
        thunkAPI.dispatch(setBulkStatus("uploading"));
      }

      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "bulk upload error"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const bulkUploadStatusPoll = createAsyncThunk(
  "products/bulkUploadStatusPoll",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${args.token}`,
          "Accept-Language": args.localization,
        },
      };
      const response = await axios.get(
        `${baseURL}/v1/admin/products/bulk/check/${args.statusToken}`,
        config
      );
      if (response.data && response.data.status === "completed") {
        // thunkAPI.dispatch(
        //   successSnack('Bulk CSV upload completed successfully')
        // );

        thunkAPI.dispatch(setBulkStatus("success"));
        return await response.data;
      } else if (
        response.data &&
        response.data.status === "completed with error(s)"
      ) {
        // let responseMessage = `Bulk upload completed with ${response.data.failures.length} errors:`;
        // if (response.data.failures.length) {
        //   response.data.failures.forEach((failure) => {
        //     responseMessage += `\nCSV Line: ${failure.csvLine} - ${failure.reason}.\n`;
        //   });
        // }
        if (response.data.failures === null) {
          response.data.failures = [
            { csvLine: "All", reason: "Check Product Status" },
          ];
        }
        thunkAPI.dispatch(setFeedback(response.data));
        thunkAPI.dispatch(setBulkStatus("success with errors"));
        // thunkAPI.dispatch(infoSnack(responseMessage));
      } else if (response.data && response.data.status === "incomplete") {
        setTimeout(() => {
          thunkAPI.dispatch(bulkUploadStatusPoll(args));
        }, 5000);
      } else
        thunkAPI.rejectWithValue({
          error: "An error occured with the bulk upload",
        });
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "bulk upload error"
          )
        );
        thunkAPI.dispatch(
          setFeedback({
            failures: [],
            errorMessage:
              error.response.data.error_description || "bulk upload error",
          })
        );
        thunkAPI.dispatch(setBulkStatus("error"));
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchBulkUploadHistory = createAsyncThunk(
  "products/fetchBulkUploadHistory",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${args.token}` },
      };
      const response = await axios.get(
        `${baseURL}/v1/admin/products/bulk/uploaded_files${args.query}`,
        config
      );

      return await response.data;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching products list"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${args.token}` },
      };
      const response = await axios.get(
        `${baseURL}/v1/admin/products${args.query}`,
        config
      );
      thunkAPI.dispatch(setProductsAmount(response.headers["x-total-count"]));
      thunkAPI.dispatch(setLastQuery(args.query));

      return await response.data;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching products list"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchMoreProducts = createAsyncThunk(
  "products/fetchMoreProducts",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${args.token}` },
      };
      const response = await axios.get(
        `${baseURL}/v1/admin/products${args.query}&offset=${args.offset}`,
        config
      );

      // thunkAPI.dispatch(setLastQuery(args.query));
      thunkAPI.dispatch(setProductsAmount(response.headers["x-total-count"]));

      return await response.data;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
      }
      if (error.response && error.response.data) {
        // thunkAPI.dispatch(
        //   errorSnack(
        //     error.response.data.error_description ||
        //       'Error fetching products list'
        //   )
        // );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchProductsByGroup = createAsyncThunk(
  "products/fetchProductsByGroup",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      const response = await axios.get(
        `${baseURL}/v1/admin/products/groups/${args.groupID}`,
        config
      );
      return await response;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching product group"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchProductByID = createAsyncThunk(
  "products/fetchProductByID",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      const response = await axios.get(
        `${baseURL}/v1/admin/products/${args.id}`,
        config
      );
      return await response.data;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching product data"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.delete(
        `${baseURL}/v1/admin/products/${args.id}`,
        config
      );
      thunkAPI.dispatch(successSnack("Product deleted successfully"));
      return await response;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "Error deleting product"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      var response;
      //full options
      if (Object.keys(args.product).length) {
        response = await axios.patch(
          `${baseURL}/v1/admin/products/${args.id}`,
          args.product,
          config
        );
      }

      const updatePromises = [];

      if (args.catIDs.length) {
        updatePromises.push(
          thunkAPI.dispatch(
            updateProductCategories({
              id: args.id,
              token: args.token,
              categoryIDs: args.catIDs,
            })
          )
        );
      }

      if (args.imageChange.imagesAdded.length) {
        args.imageChange.imagesAdded.forEach((image) => {
          updatePromises.push(
            thunkAPI.dispatch(
              preSignedURLMedia({
                token: args.token,
                image: image,
                id: args.id,
                contentType: "image/jpeg",
                action: "updated",
                message: true,
              })
            )
          );
        });
      }

      if (args.imageChange.imagesRemoved.length) {
        args.imageChange.imagesRemoved.forEach((image) => {
          updatePromises.push(
            thunkAPI.dispatch(
              deleteProductImage({
                token: args.token,
                id: args.id,
                imageID: image.id,
              })
            )
          );
        });
      }

      if (args.imageChange.imagesUpdated.length) {
        args.imageChange.imagesUpdated.forEach((image) => {
          updatePromises.push(
            thunkAPI.dispatch(
              updateProductImage({
                token: args.token,
                id: args.id,
                imageID: image.id,
                productImage: {
                  title: image.title,
                  altText: image.altText,
                },
              })
            )
          );
        });
      }

      if (args.excludedClients.length) {
        args.excludedClients.forEach((client) => {
          updatePromises.push(
            thunkAPI.dispatch(
              deleteClientFromProduct({
                token: args.token,
                clientID: client,
                id: args.id,
              })
            )
          );
        });
      }

      if (args.includedClients.length) {
        args.includedClients.forEach((client) => {
          updatePromises.push(
            thunkAPI.dispatch(
              includeClientFromProduct({
                token: args.token,
                clientID: client,
                id: args.id,
              })
            )
          );
        });
      }

      await Promise.all(updatePromises);

      if (args.imageChange.imagesAdded.length || args.catIDs.length) {
        thunkAPI.dispatch(
          infoSnack(
            `Updating ${
              args.imageChange.imagesAdded.length
                ? "product images"
                : "product categories"
            }`
          )
        );
      } else {
        thunkAPI.dispatch(successSnack("Product updated successfully"));
        if (args.groupID) {
          if (args.firstInGroup) {
            console.log("Dispatching Here");
            if (args.groupID) {
              thunkAPI.dispatch(
                fetchProductsByGroup({
                  token: args.token,
                  groupID: args.groupID.groupID,
                })
              );
            }
            thunkAPI.dispatch(
              fetchProductByID({ token: args.token, id: args.id })
            );
          }
        } else {
          console.log("Dispatching there");
          thunkAPI.dispatch(
            fetchProductByID({ token: args.token, id: args.id })
          );
        }

        thunkAPI.dispatch(
          fetchProducts({ token: args.token, query: "?limit=25" })
        );
      }

      return await response;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "Error updating product"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);


// export const updateProduct = createAsyncThunk(
//   "products/updateProduct",
//   async (args, thunkAPI) => {
//     const config = {
//       headers: { Authorization: `Bearer ${args.token}` },
//     };
//     try {
//       var response;
//       //full options
//       if (Object.keys(args.product).length) {
//         response = await axios.patch(
//           `${baseURL}/v1/admin/products/${args.id}`,
//           args.product,
//           config
//         );
//       }

//       if (args.catIDs.length) {
//         thunkAPI.dispatch(
//           updateProductCategories({
//             id: args.id,
//             token: args.token,
//             categoryIDs: args.catIDs,
//           })
//         );
//       }

//       if (args.imageChange.imagesAdded.length) {
//         args.imageChange.imagesAdded.forEach((image) => {
//           thunkAPI.dispatch(
//             preSignedURLMedia({
//               token: args.token,
//               image: image,
//               id: args.id,
//               contentType: "image/jpeg",
//               action: "updated",
//               message: true,
//             })
//           );
//         });
//       }

//       if (args.imageChange.imagesRemoved.length) {
//         args.imageChange.imagesRemoved.forEach((image) => {
//           thunkAPI.dispatch(
//             deleteProductImage({
//               token: args.token,
//               id: args.id,
//               imageID: image.id,
//             })
//           );
//         });
//       }

//       if (args.imageChange.imagesUpdated.length) {
//         args.imageChange.imagesUpdated.forEach((image) => {
//           thunkAPI.dispatch(
//             updateProductImage({
//               token: args.token,
//               id: args.id,
//               imageID: image.id,
//               productImage: {
//                 title: image.title,
//                 altText: image.altText,
//               },
//             })
//           );
//         });
//       }

//       if (args.excludedClients.length) {
//         args.excludedClients.forEach((client) => {
//           thunkAPI.dispatch(
//             deleteClientFromProduct({
//               token: args.token,
//               clientID: client,
//               id: args.id,
//             })
//           );
//         });
//       }

//       if (args.includedClients.length) {
//         args.includedClients.forEach((client) => {
//           thunkAPI.dispatch(
//             includeClientFromProduct({
//               token: args.token,
//               clientID: client,
//               id: args.id,
//             })
//           );
//         });
//       }

//       if (args.imageChange.imagesAdded.length || args.catIDs.length) {
//         thunkAPI.dispatch(
//           infoSnack(
//             `Updating ${
//               args.imageChange.imagesAdded.length
//                 ? "product images"
//                 : "product categories"
//             }`
//           )
//         );
//       } else {
//         thunkAPI.dispatch(successSnack("Product updated successfully"));
//         if (args.groupID) {
//           if (args.firstInGroup) {
//             console.log("Dispatching Here");
//             if (args.groupID) {
//               thunkAPI.dispatch(
//                 fetchProductsByGroup({
//                   token: args.token,
//                   groupID: args.groupID.groupID,
//                 })
//               );
//             }
//             thunkAPI.dispatch(
//               fetchProductByID({ token: args.token, id: args.id })
//             );
//           }
//         } else {
//           console.log("Dispatching there");
//           thunkAPI.dispatch(
//             fetchProductByID({ token: args.token, id: args.id })
//           );
//         }

//         thunkAPI.dispatch(
//           fetchProducts({ token: args.token, query: "?limit=25" })
//         );
//       }

//       return await response;
//     } catch (error) {
//       if (error.message.includes("401")) {
//         localStorage.removeItem("coreUser");
//         localStorage.removeItem("coreToken");
//         thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
//       } else if (error.response && error.response.data) {
//         thunkAPI.dispatch(
//           errorSnack(
//             error.response.data.error_description || "Error updating product"
//           )
//         );
//       }
//       return thunkAPI.rejectWithValue({ error: error.message });
//     }
//   }
// );


export const bulkStatusUpdate = createAsyncThunk(
  "products/bulkStatusUpdate",
  async (args, thunkAPI) => {
    Promise.all(
      args.ids.map((id) =>
        thunkAPI.dispatch(
          productBulkStatusUpdate({
            id: id,
            token: args.token,
            product: { statusID: args.statusID },
          })
        )
      )
    )
      .then((results) => {
        let bulkOutcomes = [];
        let failures = [];
        results.forEach((result) => {
          if (result.error) {
            failures.push({
              id: result.meta.arg ? result.meta.arg.id : "",
              reason: result.payload,
            });
          }
        });
        if (failures.length === 0) {
          thunkAPI.dispatch(
            successSnack("Bulk status update completed successfully")
          );
        } else if (failures.length === results.length) {
          // thunkAPI.dispatch(setFeedback({ status:"completed with error(s)",successCount:results.length - failures.length, failures: failures }))
          // thunkAPI.dispatch(setBulkDetails({name: "Bulk status"}))
          // thunkAPI.dispatch(setBulkStatus('success with errors'))
          thunkAPI.dispatch(errorSnack(`Error updating product statuses`));
        } else if (failures.length > 0) {
          // thunkAPI.dispatch(setFeedback({ status:"completed with error(s)",successCount:results.length - failures.length, failures: failures }))
          // thunkAPI.dispatch(setBulkDetails({name: "Bulk status"}))
          // thunkAPI.dispatch(setBulkStatus('success with errors'))
          thunkAPI.dispatch(
            infoSnack(`Completed with ${failures.length} error(s)`)
          );
        }

        return failures;
      })
      .catch((err) => {
        thunkAPI.dispatch(errorSnack(`Error updating product statuses`));

        return thunkAPI.rejectWithValue("Error during bulk status update");
      });
  }
);

export const productBulkStatusUpdate = createAsyncThunk(
  "products/productBulkStatusUpdate",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.patch(
        `${baseURL}/v1/admin/products/${args.id}`,
        args.product,
        config
      );

      return await response;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        return thunkAPI.rejectWithValue(
          error.response.data.error_description || "Error updating product"
        );
      } else {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
    }
  }
);

export const bulkCategoryUpdate = createAsyncThunk(
  "products/bulkCategoryUpdate",
  async (args, thunkAPI) => {
    Promise.all(
      args.ids.map((id) =>
        thunkAPI.dispatch(
          updateProductCategories({
            id: id,
            token: args.token,
            categoryIDs: args.categoryIDs,
          })
        )
      )
    )
      .then((results) => {
        results.forEach((result) => {});

        thunkAPI.dispatch(
          fetchProducts({ token: args.token, query: "?limit=25" })
        );
        thunkAPI.dispatch(successSnack(`Bulk category update successful`));

        return results;
      })
      .catch((err) => {
        thunkAPI.dispatch(errorSnack(`Error updating product statuses`));

        return thunkAPI.rejectWithValue("Error during bulk category update");
      });
  }
);

export const createProduct = createAsyncThunk(
  "products/createProduct",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      const response = await axios.post(
        `${baseURL}/v1/admin/products`,
        args.product,
        config
      );

      const newID = response.data.id;

      //product id required before category, images and excluded clients call can be made
      if (args.catIDs.length) {
        thunkAPI.dispatch(
          createProductCategories({
            token: args.token,
            id: newID,
            categoryIDs: args.catIDs,
            action: "created",
          })
        );
      }
      if (args.images.length) {
        args.images.forEach((image) => {
          thunkAPI.dispatch(
            preSignedURLMedia({
              token: args.token,
              image: image,
              id: newID,
              contentType: "image/jpeg",
              action: "created",
              message: true,
            })
          );
        });
      }
      if (args.excludedClients.length) {
        args.excludedClients.forEach((client) => {
          thunkAPI.dispatch(
            deleteClientFromProduct({
              token: args.token,
              clientID: client,
              id: newID,
            })
          );
        });
      }
      if (args.images.length || args.catIDs.length) {
        thunkAPI.dispatch(
          infoSnack(
            `Creating ${
              args.images.length ? "product images" : "product categories"
            }`
          )
        );
      } else {
        thunkAPI.dispatch(successSnack("Product created successfully"));
        thunkAPI.dispatch(fetchProductByID({ token: args.token, id: args.id }));

        // thunkAPI.dispatch(
        //   fetchProducts({ token: args.token, query: "?limit=500" })
        // );
      }

      return await response;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "Error creating product"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const createProductCategories = createAsyncThunk(
  "products/createProductCategories",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${args.token}` },
      };
      const response = await axios.post(
        `${baseURL}/v1/admin/products/${args.id}/categories`,
        { categoryIDs: args.categoryIDs },
        config
      );
      args.message &&
        thunkAPI.dispatch(successSnack(`Product ${args.action} successfully`));

      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error adding product categories"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const updateProductCategories = createAsyncThunk(
  "products/updateProductCategories",
  async (args, thunkAPI) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${args.token}` },
      };
      const response = await axios.put(
        `${baseURL}/v1/admin/products/${args.id}/categories`,
        { categoryIDs: args.categoryIDs },
        config
      );
      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.rejectWithValue(
          error.response.data.error_description ||
            "Error adding product categories"
        );
      } else if (error.message) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue("Error updating product categories");
      }
    }
  }
);

export const deleteProductCategories = createAsyncThunk(
  "products/deleteProductCategories",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.delete(
        `${baseURL}/v1/admin/products/${args.id}/categories/${args.catID}`,
        config
      );
      return await response;
    } catch (error) {
      if (error.response && error.response.data) {
        // thunkAPI.dispatch(
        //   errorSnack(
        //     error.response.data.error_description ||
        //       'Error deleting product categories'
        //   )
        // );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const priceCalculator = createAsyncThunk(
  "products/priceCalculator",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.post(
        `${baseURL}/test_price_cal`,
        args.values,
        config
      );

      return await response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description || "Error calculating price"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const includeClientFromProduct = createAsyncThunk(
  "products/includeClientFromProduct",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.delete(
        `${baseURL}/v1/admin/products/${args.id}/clients/${args.clientID}`,
        config
      );
      return await response;
    } catch (error) {
      if (error.response && error.response.data) {
        // thunkAPI.dispatch(
        //   errorSnack(
        //     error.response.data.error_description ||
        //       'Error deleting product categories'
        //   )
        // );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    } 
  }
);

export const deleteClientFromProduct = createAsyncThunk(
  "products/deleteClientFromProduct",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      //full options
      const response = await axios.post(
        `${baseURL}/v1/admin/products/${args.id}/clients/${args.clientID}`,
        {},
        config
      );
      return await response;
    } catch (error) {
      if (error.response && error.response.data) {
        // thunkAPI.dispatch(
        //   errorSnack(
        //     error.response.data.error_description ||
        //       'Error deleting product categories'
        //   )
        // );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    } 
  }
);

export const fetchProductHistory = createAsyncThunk(
  "products/fetchProductHistory",
  async (args, thunkAPI) => {
    const config = {
      headers: { Authorization: `Bearer ${args.token}` },
    };
    try {
      const response = await axios.get(
        `${baseURL}/v1/admin/products/${args.id}/history`,
        config
      );
      return await response.data;
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("coreUser");
        localStorage.removeItem("coreToken");
        thunkAPI.dispatch(infoSnack("Session expired, please log in again"));
      } else if (error.response && error.response.data) {
        thunkAPI.dispatch(
          errorSnack(
            error.response.data.error_description ||
              "Error fetching product history data"
          )
        );
      }
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getFullList = createAsyncThunk(
  "products/getFullList",
  async (args, thunkAPI) => {
    let objToMap = [];
    for (let i = 0; i < (args.targetLength - args.offset) / 100; i++) {
      objToMap.push({
        token: args.token,
        query: args.query,
        offset: args.offset + 100 * i,
      });
    }

    Promise.all(
      objToMap.map((requestData) =>
        thunkAPI.dispatch(fetchMoreProducts(requestData))
      )
    )
      .then((results) => {})
      .catch((err) => {
        return thunkAPI.rejectWithValue("Error fetching full product list");
      });
  }
);

const headCells = [
  {
    id: "images",
    checked: true,
    numeric: false,
    disablePadding: false,
    label: "Image",
    long: false,
  },
  {
    id: "sku",
    checked: true,
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "SKU",
    long: false,
  },
  {
    id: "name",
    checked: true,
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Product Name",
    long: false,
  },
  {
    id: "longDescription",
    checked: true,
    numeric: false,
    disablePadding: false,
    label: "Description",
    long: true,
  },
  {
    id: "supplierName",
    checked: true,
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Supplier",
    long: false,
  },
  {
    id: "categories",
    checked: true,
    numeric: false,
    disablePadding: false,
    label: "Categories",
    long: true,
  },

  {
    id: "brandName",
    checked: true,
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Brand",
    long: false,
  },
  {
    id: "rrp",
    checked: true,
    numeric: true,
    sortable: true,
    disablePadding: false,
    label: "RRP",
    long: false,
  },
  {
    id: "costPrice",
    checked: true,
    numeric: true,
    sortable: true,
    disablePadding: false,
    label: "Cost Price",
    long: false,
  },
  {
    id: "sellingPrice",
    checked: true,
    numeric: true,
    sortable: true,
    disablePadding: false,
    label: "Selling Price",
    long: false,
  },
  {
    id: "freight",
    checked: true,
    numeric: true,
    disablePadding: false,
    label: "Freight",
    long: false,
  },
  // {
  //   id: 'excludedSites',
  //    checked: true,
  // numeric: false,
  //   sortable: false,
  //   disablePadding: false,
  //   label: 'Excluded Sites',

  // },
  {
    id: "margin",
    checked: true,
    numeric: true,
    sortable: true,
    disablePadding: false,
    label: "Margin (%)",
    long: false,
  },
  {
    id: "updatedAt",
    checked: true,
    numeric: true,
    disablePadding: false,
    label: "Last Updated",
    long: false,
    sortable: true,
    dates: true,
  },
  {
    id: "createdAt",
    checked: true,
    numeric: true,
    disablePadding: false,
    label: "Created",
    long: false,
    sortable: true,
    dates: true,
  },
  {
    id: "statusID",
    checked: true,
    numeric: true,
    sortable: true,
    disablePadding: false,
    label: "Status",
    long: false,
  },
];

const initial = {
  priceCalculation: { sellingPrice: "" },
  product: {},
  productList: { data: [] },
  productGroupList: { data: [] },
  productHistory: [],
  loading: "idle",
  message: "",
  lastQuery: "",
  error: "",
  feedback: { failures: [] },
  bulkDetails: { name: "" },
  bulkStatus: "",
  productReady: false,
  headCells: headCells,
  url: "",
  uploadedFiles: [],
  productsAmount: "",
};

const productsSlice = createSlice({
  name: "products",
  initialState: initial,
  reducers: {
    resetProduct(state, action) {
      state.productList = { data: [] };
      state.product = {};
      state.loading = "idle";
      state.priceCalculation = { sellingPrice: "" };
      state.productReady = false;
    },
    setFeedback: (state, action) => {
      state.feedback = action.payload;
    },
    setBulkDetails: (state, action) => {
      state.bulkDetails = action.payload;
    },
    setBulkStatus: (state, action) => {
      state.bulkStatus = action.payload;
    },
    resetBulk: (state, action) => {
      state.bulkStatus = "";
      state.bulkDetails = { name: "" };
      state.feedback = { failures: [] };
    },
    setLastQuery: (state, action) => {
      state.lastQuery = action.payload;
    },
    setProductsAmount: (state, action) => {
      state.productsAmount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state) => {
      state.productList = { data: [] };
      // state.loading = "loading";
    });
    builder.addCase(fetchProducts.fulfilled, (state, { payload }) => {
      state.error = "";
      state.productList = payload;
      state.loading = "loaded";
    });
    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(fetchMoreProducts.fulfilled, (state, { payload }) => {
      state.error = "";
      state.productList = {
        ...state.productList,
        data: state.productList.data.concat(payload.data),
      };
      state.loading = "loaded";
    });
    builder.addCase(fetchMoreProducts.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(getFullList.fulfilled, (state, { payload }) => {
      state.error = "";
      state.productList = {
        ...state.productList,
        data: state.productList.data.sort(function (a, b) {
          return a.id - b.id;
        }),
      };
      state.loading = "loaded";
    });
    builder.addCase(getFullList.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(preSignedURL.pending, (state) => {
      state.url = "";
      state.loading = "loading";
      state.editing = false;
    });
    builder.addCase(preSignedURL.fulfilled, (state, { payload }) => {
      state.url = payload.preSignedURL;
      state.loading = "loaded";
      state.editing = false;
    });
    builder.addCase(preSignedURL.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(fetchBulkUploadHistory.pending, (state) => {
      state.uploadedFiles = [];
      state.loading = "loading";
    });
    builder.addCase(fetchBulkUploadHistory.fulfilled, (state, { payload }) => {
      state.error = "";
      state.uploadedFiles = payload;
      state.loading = "loaded";
    });
    builder.addCase(fetchBulkUploadHistory.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(fetchProductsByGroup.pending, (state) => {
      state.productGroupList = { data: [] };
      // state.loading = "loading";
    });
    builder.addCase(fetchProductsByGroup.fulfilled, (state, { payload }) => {
      state.error = "";
      state.productGroupList = payload;
      // state.loading = "loaded";
    });
    builder.addCase(fetchProductsByGroup.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(fetchProductHistory.pending, (state) => {
      state.productHistory = [];
      // state.loading = "loading";
      // state.productReady = false;
    });
    builder.addCase(fetchProductHistory.fulfilled, (state, { payload }) => {
      state.error = "";
      state.productHistory = payload;
      // state.loading = "loaded";
      // state.productReady = true;
    });
    builder.addCase(fetchProductHistory.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
      state.productReady = false;
    });
    builder.addCase(fetchProductByID.pending, (state) => {
      // state.product = {};
      state.priceCalculation = { sellingPrice: "" };
      state.loading = "loading";
      state.productReady = false;
    });
    builder.addCase(fetchProductByID.fulfilled, (state, { payload }) => {
      state.error = "";
      state.product = payload;
      state.loading = "loaded";
      state.productReady = true;
    });
    builder.addCase(fetchProductByID.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
      state.productReady = false;
    });
    builder.addCase(deleteProduct.pending, (state) => {
      state.message = "";
      state.loading = "loading";
    });
    builder.addCase(deleteProduct.fulfilled, (state, { payload }) => {
      state.error = "";
      state.message = payload;
      state.loading = "idle";
    });
    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(updateProduct.pending, (state) => {
      state.message = "";
      state.loading = "loading";
    });
    builder.addCase(updateProduct.fulfilled, (state, { payload }) => {
      state.error = "";
      // state.message = payload;
      state.loading = "idle";
    });
    builder.addCase(updateProduct.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(createProduct.pending, (state) => {
      state.message = "";
      state.loading = "loading";
    });
    builder.addCase(createProduct.fulfilled, (state, { payload }) => {
      state.error = "";
      // state.message = payload;
      state.loading = "idle";
    });
    builder.addCase(createProduct.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });

    builder.addCase(deleteProductCategories.pending, (state) => {
      state.message = "";
      state.loading = "loading";
    });
    builder.addCase(deleteProductCategories.fulfilled, (state, { payload }) => {
      state.error = "";
      // state.message = payload;
      state.loading = "loaded";
    });
    builder.addCase(deleteProductCategories.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });

    builder.addCase(createProductCategories.pending, (state) => {
      state.message = "";
      state.loading = "loading";
    });
    builder.addCase(createProductCategories.fulfilled, (state, { payload }) => {
      state.error = "";
      // state.message = payload;
      state.loading = "loaded";
    });
    builder.addCase(createProductCategories.rejected, (state, action) => {
      state.loading = "error";
      state.error = action.error.message;
    });
    builder.addCase(priceCalculator.pending, (state) => {
      // state.priceCalculation = {};
      // state.loading = 'loading';
    });
    builder.addCase(priceCalculator.fulfilled, (state, { payload }) => {
      // state.error = '';
      state.priceCalculation = payload;
      // state.loading = 'loaded';
    });
  },
});

// Action creators are generated for each case reducer function
export const {
  setFeedback,
  setBulkDetails,
  setBulkStatus,
  resetBulk,
  setLastQuery,
  setHeadCells,
  setProductsAmount,
} = productsSlice.actions;

export const selectProducts = createSelector(
  (state) => ({
    products: state.products,
    productList: state.products.productList,
    productGroupList: state.products.productGroupList,
    productHistory: state.products.productHistory,
    loading: state.products.loading,
    error: state.products.error,
    product: state.products.product,
    priceCalculation: state.products.priceCalculation,
    bulkStatus: state.products.bulkStatus,
    bulkDetails: state.products.bulkDetails,
    feedback: state.products.feedback,
    lastQuery: state.products.lastQuery,
    productReady: state.products.productReady,
    headCells: state.products.headCells,
    url: state.products.url,
    uploadedFiles: state.products.uploadedFiles,
    productsAmount: state.products.rowCount,
  }),
  (state) => state
);
export default productsSlice.reducer;
